import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import searchIconGray from 'src/assets/svg/searchIconGray.svg'
import { AD_ANALYSIS_LIBRARY_ROUTE } from 'src/constants/routes'
import { useSaveMonitoringPageMutation } from 'src/reactQuery/useSaveMonitoringPageMutation'
import { useSearchAdsLibraryPagesQuery } from 'src/reactQuery/useSearchAdsLibraryPagesQuery'
import { isNumericPageId } from 'src/utils/isNumericPageId'
import classes from './searchDropdown.module.scss'

interface SearchDropdownProps {
  adCategory?: string | null
  onClose: () => void
  query: string
}

export const SearchDropdown: FC<SearchDropdownProps> = ({
  adCategory,
  onClose,
  query,
}) => {
  const navigate = useNavigate()
  const { data, isLoading } = useSearchAdsLibraryPagesQuery(query)
  const saveMutation = useSaveMonitoringPageMutation()

  const pages = data?.data || []

  const handleAdvertiserClick = (pageId: string, pageName?: string) => {
    const isPolitical = adCategory === 'Issues, elections or politics'
    const url = isPolitical
      ? `${AD_ANALYSIS_LIBRARY_ROUTE}/${pageId}?adType=POLITICAL_AND_ISSUE_ADS`
      : `${AD_ANALYSIS_LIBRARY_ROUTE}/${pageId}`
    navigate(url, { state: { pageName } })
    onClose()
  }

  // page_id must be the numeric Facebook id from search results; the backend
  // resolves page_name/picture/category server-side, so we no longer send them.
  // Do NOT pin ad_reached_countries here — the backend defaults to all
  // countries, and forcing 'GE' makes the comparison query return 0 ads for
  // advertisers that don't target Georgia (matching the detail-page save).
  const handleSave = (e: React.MouseEvent, pageId: string) => {
    e.stopPropagation()
    if (!isNumericPageId(pageId)) return
    saveMutation.mutate({
      ad_type:
        adCategory === 'Issues, elections or politics'
          ? 'POLITICAL_AND_ISSUE_ADS'
          : 'ALL',
      page_id: pageId,
    })
  }

  return (
    <div className={classes.dropdown}>
      <div className={classes.exactPhrase}>
        <div className={classes.exactPhraseIconWrapper}>
          <img
            alt="search"
            className={classes.exactPhraseIcon}
            src={searchIconGray}
          />
        </div>
        <div className={classes.exactPhraseText}>
          <span className={classes.exactPhraseQuery}>
            &ldquo;{query}&rdquo;
          </span>
          <span className={classes.exactPhraseLabel}>
            Search this exact phrase
          </span>
        </div>
      </div>

      <div className={classes.divider} />

      <div className={classes.advertisersSection}>
        <h3 className={classes.advertisersTitle}>Advertisers</h3>
        {isLoading ? (
          <div className={classes.loadingText}>Searching...</div>
        ) : pages.length === 0 ? (
          <div className={classes.loadingText}>No advertisers found</div>
        ) : (
          <div className={classes.advertisersList}>
            {pages.map((page) => (
              <div
                className={classes.advertiserCard}
                key={page.id}
                onClick={() => handleAdvertiserClick(page.id, page.name)}
              >
                <img
                  alt={page.name || ''}
                  className={classes.advertiserLogo}
                  src={
                    page.picture?.data?.url ||
                    `https://graph.facebook.com/${page.id}/picture?type=square`
                  }
                />
                <div className={classes.advertiserInfo}>
                  <span className={classes.advertiserName}>{page.name}</span>
                  <div className={classes.advertiserRow}>
                    <FacebookIcon className={classes.socialIcon} />
                    <span className={classes.advertiserHandle}>
                      {page.fan_count
                        ? `${(page.fan_count / 1000).toFixed(1)}K followers`
                        : ''}
                    </span>
                    <span className={classes.advertiserCategory}>
                      {page.category}
                    </span>
                  </div>
                </div>
                <button
                  className={classes.saveBtn}
                  disabled={!isNumericPageId(page.id) || saveMutation.isPending}
                  onClick={(e) => handleSave(e, page.id)}
                  title={
                    isNumericPageId(page.id)
                      ? undefined
                      : 'Only pages found by URL, @handle, or ID can be saved'
                  }
                  type="button"
                >
                  {saveMutation.isPending ? '...' : '+ Save'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
